import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';

import { AuthService } from './auth.service';
import { RealtimeService } from '../realtime/realtime.service';
import { UserNotification, UserNotificationList } from '../models/notification.model';

/**
 * Persistente Inbox + Live-Aktualisierung:
 * <ul>
 *   <li>Liest beim Login einmalig den Unread-Count und hält ihn als Signal.</li>
 *   <li>Hört auf <c>notification.created</c>-Events aus dem
 *       <see cref="RealtimeService"/> und inkrementiert den Counter ohne Reload.</li>
 *   <li>Bietet GET/POST-Wrapper für die /benachrichtigungen-Page und das Bell-Dropdown.</li>
 * </ul>
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly realtime = inject(RealtimeService);
  private readonly platformId = inject(PLATFORM_ID);

  readonly unreadCount = signal(0);
  /** Schubladen-Daten für das Header-Dropdown — letzte 8 Einträge. */
  readonly recent = signal<UserNotification[]>([]);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    // Beim Login: Counter + recent laden. Bei Logout zurücksetzen.
    this.auth.currentUser$.subscribe((u) => {
      if (u) {
        void this.refresh();
      } else {
        this.unreadCount.set(0);
        this.recent.set([]);
      }
    });

    // Server-Push tickt den Counter sofort hoch und re-fetcht das Dropdown.
    // Wir hängen an *jedem* Event (nicht nur notification.created), damit
    // ältere Konsumenten (Cockpit-Refresh etc.) weiter funktionieren — beim
    // Bell-Refresh interessiert uns ohnehin nur 'notification.created'.
    this.realtime.events$.subscribe((evt) => {
      if (evt.type === 'notification.created') {
        void this.refresh();
      }
    });
  }

  list(skip = 0, take = 30, unreadOnly = false): Observable<UserNotificationList> {
    const params = new URLSearchParams();
    params.set('skip', String(skip));
    params.set('take', String(take));
    if (unreadOnly) params.set('unreadOnly', 'true');
    return this.http.get<UserNotificationList>(`/api/benachrichtigungen?${params}`);
  }

  markRead(id: string): Observable<void> {
    return this.http.post<void>(`/api/benachrichtigungen/${id}/read`, {});
  }

  markAllRead(): Observable<{ marked: number }> {
    return this.http.post<{ marked: number }>(`/api/benachrichtigungen/alle-gelesen`, {});
  }

  async refresh(): Promise<void> {
    try {
      const list = await firstValueFrom(this.list(0, 8, false));
      this.recent.set(list.items);
      this.unreadCount.set(list.unreadCount);
    } catch {
      // Stumm: User ist evtl. abgemeldet (401) — der Auth-Subscribe oben
      // setzt den State sowieso zurück.
    }
  }

  /**
   * Optimistic mark-as-read: setzt den lokalen Counter sofort runter und
   * postet im Hintergrund. Im Fehlerfall macht der nächste refresh()-Tick
   * die UI wieder konsistent.
   */
  async markReadOptimistic(id: string): Promise<void> {
    const before = this.recent();
    const updated = before.map((n) => (n.id === id ? { ...n, read: true } : n));
    this.recent.set(updated);
    if (this.unreadCount() > 0 && before.find((n) => n.id === id && !n.read)) {
      this.unreadCount.set(this.unreadCount() - 1);
    }
    try {
      await firstValueFrom(this.markRead(id));
    } catch {
      void this.refresh();
    }
  }
}
